import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../services/api';
import { WORKFLOW_STATUSES, INTERNAL_STATUSES, VENDOR_WORKFLOW_STATUSES, VENDOR_TYPES, SALE_DESTINATIONS } from './recon/constants';
import { QRCodePopup } from './VehicleQRCode';
import { VehicleFilesButton } from './VehicleFilesModal';
import VinDisplay from './shared/VinDisplay';

// Multi-select status component
const MultiStatusSelect = ({ vehicle, statuses, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Ensure statuses is always an array and dedupe
  const selectedStatuses = Array.isArray(statuses) ? [...new Set(statuses)] : [statuses || 'awaiting_inspection'];
  
  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  const toggleStatus = (statusValue) => {
    let newStatuses;
    if (selectedStatuses.includes(statusValue)) {
      // Remove status (but keep at least one)
      newStatuses = selectedStatuses.filter(s => s !== statusValue);
      if (newStatuses.length === 0) newStatuses = ['awaiting_inspection'];
    } else {
      // Add status
      newStatuses = [...selectedStatuses, statusValue];
    }
    onChange(newStatuses);
  };
  
  // Get display text for selected statuses
  const getDisplayText = () => {
    if (selectedStatuses.length === 0) return 'Select...';
    if (selectedStatuses.length === 1) {
      return WORKFLOW_STATUSES.find(s => s.value === selectedStatuses[0])?.label || selectedStatuses[0];
    }
    return `${selectedStatuses.length} statuses`;
  };
  
  // Get color class based on statuses
  const getColorClass = () => {
    if (selectedStatuses.includes('ready_for_sale')) return 'text-green-700 bg-green-50';
    if (selectedStatuses.includes('at_detail')) return 'text-blue-700 bg-blue-50';
    if (selectedStatuses.some(s => s.includes('needs_'))) return 'text-orange-700 bg-orange-50';
    if (selectedStatuses.includes('repairs_in_progress')) return 'text-yellow-700 bg-yellow-50';
    if (selectedStatuses.includes('parts_ordered')) return 'text-blue-700 bg-blue-50';
    return '';
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`text-xs px-2 py-1 rounded border border-neutral-200 bg-white cursor-pointer text-left w-full max-w-[160px] flex items-center justify-between gap-1 ${
          disabled ? 'opacity-50' : ''
        } ${getColorClass()}`}
      >
        <span className="truncate">{getDisplayText()}</span>
        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 w-56 max-h-64 overflow-y-auto">
          {WORKFLOW_STATUSES.map(status => (
            <label
              key={status.value}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-neutral-50 cursor-pointer text-xs"
            >
              <input
                type="checkbox"
                checked={selectedStatuses.includes(status.value)}
                onChange={() => toggleStatus(status.value)}
                className="rounded text-blue-600"
              />
              <span>{status.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const WorkflowModule = ({ vehicles, onRefresh, onSilentRefresh, onSelectVehicle, currentUser, onNavigateToRO, onNavigateToPartsRoom }) => {
  const [workflowVehicles, setWorkflowVehicles] = useState([]);
  const [vendorNeeds, setVendorNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryTab, setCategoryTab] = useState(() => localStorage.getItem('workflow_tab') || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendorType, setSelectedVendorType] = useState(null);
  const [punchList, setPunchList] = useState([]);
  const [loadingPunchList, setLoadingPunchList] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [savingField, setSavingField] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null); // { vehicle, notes }
  const [costPrompt, setCostPrompt] = useState(null);
  const [paymentBatch, setPaymentBatch] = useState([]); // [{ vehicle, vendorType, amount }]
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [locations, setLocations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('workflow_locations') || '[]');
    } catch { return []; }
  });
  const [runList, setRunList] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('workflow_runlist') || '[]');
    } catch { return []; }
  });
  const [customDestinations, setCustomDestinations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('workflow_custom_destinations') || '[]');
    } catch { return []; }
  });
  const [showRunList, setShowRunList] = useState(false);
  const [addToListVehicle, setAddToListVehicle] = useState(null);
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [workList, setWorkList] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('workflow_worklist') || '[]');
    } catch { return []; }
  });
  const [showWorkList, setShowWorkList] = useState(false);
  const [addToWorkListVehicle, setAddToWorkListVehicle] = useState(null);
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [hiddenStatuses, setHiddenStatuses] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('workflow_hidden') || '[]');
    } catch { return []; }
  });
  const [showOnlyStatuses, setShowOnlyStatuses] = useState([]); // array of status values to show (empty = show all)
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [readyExpanded, setReadyExpanded] = useState(false);
  const [crFilter, setCrFilter] = useState('all'); // 'all', 'missing', 'complete'
  const [creatingRO, setCreatingRO] = useState(null);
  const [adminTodos, setAdminTodos] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('workflow_admin_todos') || '[]');
    } catch { return []; }
  });
  const [todoVehicle, setTodoVehicle] = useState(null);

  // Category definitions
  const CATEGORIES = {
    all: { label: 'All', statuses: null },
    awaiting: { label: 'Awaiting', statuses: ['awaiting_inspection'] },
    in_progress: { label: 'In Progress', statuses: ['parts_ordered', 'repairs_in_progress'] },
    vendor_work: { label: 'Vendor Work', statuses: ['needs_outside_vendor', 'needs_inhouse_body', 'needs_outsource_body', 'needs_outside_repair', 'need_new_vendor'] },
    detail: { label: 'Detail', statuses: ['at_detail'] },
    ready: { label: 'Ready', statuses: ['ready_for_sale'] }
  };

  // Save tab, locations, run list, and custom destinations to localStorage
  useEffect(() => { localStorage.setItem('workflow_tab', categoryTab); }, [categoryTab]);
  useEffect(() => { localStorage.setItem('workflow_hidden', JSON.stringify(hiddenStatuses)); }, [hiddenStatuses]);
  useEffect(() => { localStorage.setItem('workflow_locations', JSON.stringify(locations)); }, [locations]);
  useEffect(() => { localStorage.setItem('workflow_runlist', JSON.stringify(runList)); }, [runList]);
  useEffect(() => { localStorage.setItem('workflow_custom_destinations', JSON.stringify(customDestinations)); }, [customDestinations]);
  useEffect(() => { localStorage.setItem('workflow_worklist', JSON.stringify(workList)); }, [workList]);
  useEffect(() => { localStorage.setItem('workflow_admin_todos', JSON.stringify(adminTodos)); }, [adminTodos]);

  // Admin Todo helpers
  const addTodo = (vehicleId, text) => {
    if (!text.trim()) return;
    setAdminTodos(prev => [...prev, { id: Date.now(), vehicleId, text: text.trim(), done: false, createdAt: new Date().toISOString() }]);
  };
  const toggleTodo = (todoId) => {
    setAdminTodos(prev => prev.map(t => t.id === todoId ? { ...t, done: !t.done } : t));
  };
  const deleteTodo = (todoId) => {
    setAdminTodos(prev => prev.filter(t => t.id !== todoId));
  };
  const getTodoCount = (vehicleId) => adminTodos.filter(t => t.vehicleId === vehicleId && !t.done).length;

  // All destinations (built-in + custom)
  const allDestinations = [...SALE_DESTINATIONS, ...customDestinations];

  // Add custom destination
  const addCustomDestination = (name) => {
    const newDest = { value: `custom_${Date.now()}`, label: name };
    setCustomDestinations(prev => [...prev, newDest]);
    setShowAddDestination(false);
  };

  // Add vehicle to run list
  const addToRunList = (vehicle, destination) => {
    const deliveredCost = parseFloat(vehicle.purchasePrice || 0) + parseFloat(vehicle.auctionFees || 0) + parseFloat(vehicle.transportCost || 0);
    const reconCost = parseFloat(vehicle.reconCost || 0);
    const tiv = deliveredCost + reconCost;
    const askingPrice = parseFloat(vehicle.askingPrice || vehicle.retailPrice || 0);
    const potentialProfit = askingPrice - tiv;
    
    // Get all statuses for display - use array if available, fallback to single status
    // Only default to 'awaiting_inspection' if no statuses exist at all
    let statuses = vehicle.reconWorkflowStatuses;
    if (!statuses || statuses.length === 0) {
      statuses = vehicle.reconWorkflowStatus ? [vehicle.reconWorkflowStatus] : ['awaiting_inspection'];
    }
    const entry = {
      id: vehicle.id,
      stockNumber: vehicle.stockNumber,
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      destination,
      needs: statuses, // Store as array
      notes: vehicle.reconGamePlan || '', // Include notes for printing
      location: vehicle.currentLocation || 'On Lot',
      addedAt: new Date().toISOString(),
      // Financial data
      deliveredCost,
      reconCost,
      tiv,
      askingPrice,
      potentialProfit
    };
    setRunList(prev => {
      // Don't add duplicates
      if (prev.some(v => v.id === vehicle.id)) return prev;
      return [...prev, entry];
    });
    setAddToListVehicle(null);
  };

  // Remove from run list
  const removeFromRunList = (vehicleId) => {
    setRunList(prev => prev.filter(v => v.id !== vehicleId));
  };

  // Update a field on a run list entry (assignee, runListNotes, etc.)
  const updateRunListField = (vehicleId, field, value) => {
    setRunList(prev => prev.map(v => v.id === vehicleId ? { ...v, [field]: value } : v));
  };

  // Clear entire run list
  const clearRunList = () => {
    if (window.confirm('Clear the entire run list?')) {
      setRunList([]);
    }
  };

  // Add a new location
  const addLocation = (locationData) => {
    const newLocation = { id: Date.now(), ...locationData };
    setLocations(prev => [...prev, newLocation]);
    setShowAddLocation(false);
  };

  // Initial load: sync vendor statuses once, then fetch data
  const hasSynced = useRef(false);
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Only sync vendor statuses on first mount, not on every refresh
        // This prevents the sync from overwriting manual status changes
        if (!hasSynced.current) {
          await api.syncVendorStatuses().catch(err => console.warn('Vendor sync failed:', err));
          hasSynced.current = true;
        }
        
        const [vehicleData, vendorNeedsData] = await Promise.all([
          api.getVehicles({ includeSold: false }),
          api.getVendorNeeds().catch(() => [])
        ]);
        setWorkflowVehicles(vehicleData);
        setVendorNeeds(vendorNeedsData);
      } catch (error) {
        console.error('Failed to load workflow data:', error);
        setWorkflowVehicles([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [vehicles]);

  // Create RO for vehicle
  const handleCreateRO = async (vehicle) => {
    setCreatingRO(vehicle.id);
    try {
      const newRO = await api.createRepairOrder({ vehicleId: vehicle.id });
      if (onRefresh) onRefresh();
      // Open the new RO in a new browser tab
      if (newRO?.id) {
        localStorage.setItem('pendingROId', newRO.id.toString());
        window.open(window.location.origin + window.location.pathname + '#recon', '_blank');
      }
      // Reload vehicles to get updated hasOpenRO status
      const vehicleData = await api.getVehicles({ includeSold: false });
      setWorkflowVehicles(vehicleData);
    } catch (error) {
      console.error('Failed to create RO:', error);
      alert('Failed to create RO: ' + error.message);
    } finally {
      setCreatingRO(null);
    }
  };

  // Filter vehicles based on current filters
  const filteredVehicles = workflowVehicles.filter(v => {
    // Exclude personal/shop cars and copart/salvage cars from workflow
    if (v.endUse === 'shop_personal' || v.endUse === 'copart' || v.endUse === 'iaai' || v.endUse === 'salvage') {
      return false;
    }

    // Category tab filter - check both single status and multi-status array
    const cat = CATEGORIES[categoryTab];
    if (cat && cat.statuses) {
      const vehicleStatuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
      const hasMatchingStatus = vehicleStatuses.some(s => cat.statuses.includes(s));
      if (!hasMatchingStatus) {
        return false;
      }
    }

    // Hidden statuses filter - check if all statuses are hidden
    const vehicleStatusesForHidden = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
    const allStatusesHidden = vehicleStatusesForHidden.every(s => hiddenStatuses.includes(s));
    if (allStatusesHidden) {
      return false;
    }

    // Show Only filter - if set, only show vehicles with at least one of the selected statuses
    if (showOnlyStatuses.length > 0) {
      const vehicleStatuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
      const hasMatchingStatus = vehicleStatuses.some(s => showOnlyStatuses.includes(s));
      if (!hasMatchingStatus) {
        return false;
      }
    }

    // Search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const searchableText = [
        v.stockNumber,
        v.vin,
        v.year,
        v.make,
        v.model,
        v.reconWorkflowStatus,
        v.reconGamePlan
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });

  // Split filtered vehicles into active (not ready) and ready for sale
  const activeVehicles = filteredVehicles.filter(v => {
    const statuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
    return !statuses.includes('ready_for_sale');
  });
  const readyVehiclesAll = filteredVehicles.filter(v => {
    const statuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
    return statuses.includes('ready_for_sale');
  });
  const readyVehicles = readyVehiclesAll.filter(v => {
    if (crFilter === 'missing') return !v.crOpenlane || !v.crAcv;
    if (crFilter === 'complete') return v.crOpenlane && v.crAcv;
    return true;
  });

  // Count vehicles by status for summary (supports multi-status)
  const statusCounts = {};
  WORKFLOW_STATUSES.forEach(s => { statusCounts[s.value] = 0; });
  workflowVehicles.filter(v => !['shop_personal', 'copart', 'iaai', 'salvage'].includes(v.endUse)).forEach(v => {
    const statuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
    statuses.forEach(status => {
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
  });

  // Count vehicles with pending vendor work (from vendorNeeds API data)
  const vendorWorkCount = vendorNeeds.reduce((sum, vn) => sum + (vn.count || 0), 0);

  // Load punch list for selected vendor type
  const loadPunchList = useCallback(async (vendorType) => {
    setLoadingPunchList(true);
    try {
      const data = await api.getVendorPunchList(vendorType);
      setPunchList(data || []);
    } catch (err) {
      console.error('Failed to load punch list:', err);
      setPunchList([]);
    } finally {
      setLoadingPunchList(false);
    }
  }, []);

  useEffect(() => {
    if (selectedVendorType) {
      loadPunchList(selectedVendorType);
    }
  }, [selectedVendorType, loadPunchList]);

  // Toggle hidden status
  const toggleHidden = (status) => {
    setHiddenStatuses(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  // Download task report as CSV
  const downloadTaskReport = () => {
    const headers = ['Stock #', 'Year', 'Make', 'Model', 'VIN', 'Status', 'Exit Strategy', 'Location', 'Game Plan'];
    const rows = filteredVehicles.map(v => {
      // Support multiple statuses
      const statuses = v.reconWorkflowStatuses || [v.reconWorkflowStatus || 'awaiting_inspection'];
      const statusLabels = statuses.map(s => WORKFLOW_STATUSES.find(ws => ws.value === s)?.label || s).join(' / ');
      return [
        v.stockNumber,
        v.year,
        v.make,
        v.model,
        v.vin,
        statusLabels,
        v.endUse || 'retail',
        v.currentLocation || '',
        (v.reconGamePlan || '').replace(/,/g, ';')
      ];
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workflow_task_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Inline field update
  const updateField = async (vehicleId, field, value) => {
    setSavingField(`${vehicleId}-${field}`);
    try {
      // For multi-status, dedupe and also send the first status as reconWorkflowStatus for backend compatibility
      let updateData = { [field]: value };
      if (field === 'reconWorkflowStatuses' && Array.isArray(value)) {
        // Dedupe the statuses array
        const deduped = [...new Set(value)];
        updateData.reconWorkflowStatuses = deduped;
        updateData.reconWorkflowStatus = deduped.length > 0 ? deduped[0] : 'awaiting_inspection';
      }
      // endUse, crOpenlane, crAcv go through the general vehicle update endpoint, not workflow
      if (field === 'endUse' || field === 'crOpenlane' || field === 'crAcv') {
        await api.updateVehicle(vehicleId, { [field]: value });
      } else {
        await api.updateVehicleWorkflow(vehicleId, updateData);
      }
      setWorkflowVehicles(prev => prev.map(v => 
        v.id === vehicleId ? { ...v, ...updateData } : v
      ));
      // Don't trigger refresh here - local state is updated and backend is saved.
      // Refreshing would re-trigger syncVendorStatuses which overwrites manual status changes.
    } catch (error) {
      console.error('Failed to update:', error);
      alert('Failed to update: ' + error.message);
    } finally {
      setSavingField(null);
    }
  };

  // Toggle notes expansion
  const toggleNotes = (vehicleId) => {
    setExpandedNotes(prev => ({ ...prev, [vehicleId]: !prev[vehicleId] }));
  };

  // Core function to mark vendor work complete (used by both modal and batch)
  const markVendorComplete = async (vehicle, vendorType, actualCost) => {
    // Map vendor type to workflow status (1:1 with VENDOR_WORKFLOW_STATUSES)
    const vendorToStatusMap = {
      'pdr': 'needs_pdr',
      'body_outside': 'needs_body_outside',
      'body_shop_outside': 'needs_body_outside',
      'body_inhouse': 'needs_body_inhouse',
      'body_shop_inhouse': 'needs_body_inhouse',
      'rim_wheel': 'needs_rim_wheel',
      'interior': 'needs_interior',
      'interior_repair': 'needs_interior',
      'glass': 'needs_glass',
      'mechanical_outside': 'needs_mechanical_outside',
      'detail': 'at_detail',
      'other': 'needs_inside_mech',
      'inside_mech': 'needs_inside_mech'
    };
    
    const statusToRemove = vendorToStatusMap[vendorType];
    
    // 1. Remove the status from vehicle's workflow statuses
    if (statusToRemove && vehicle.id) {
      const currentStatuses = vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'awaiting_inspection'];
      const newStatuses = currentStatuses.filter(s => s !== statusToRemove);
      // If no statuses left, set to awaiting_inspection
      const finalStatuses = newStatuses.length > 0 ? newStatuses : ['awaiting_inspection'];
      
      await api.updateVehicleWorkflow(vehicle.id, {
        reconWorkflowStatuses: finalStatuses,
        reconWorkflowStatus: finalStatuses[0]
      });
    }
    
    // 2. Mark vendor complete in RO and record cost (if RO exists)
    if (vehicle.roId) {
      try {
        await api.markVendorComplete(vehicle.roId, vendorType, parseFloat(actualCost) || 0);
      } catch (roErr) {
        console.warn('Failed to update RO vendor:', roErr);
      }
    }
  };

  // Handle cost prompt submission - mark vendor work complete (from modal)
  const handleCostSubmit = async (actualCost, dontKnow) => {
    if (!costPrompt) return;
    const { vehicle, vendorType } = costPrompt;
    
    try {
      if (dontKnow) {
        // Create admin reminder
        await api.createTask({
          title: `Enter cost for ${vendorType} work on ${vehicle.stockNumber}`,
          description: `Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}\nVendor Type: ${vendorType}`,
          priority: 'high',
          assignedTo: null // Admin
        }).catch(() => {});
      }
      
      await markVendorComplete(vehicle, vendorType, actualCost);
      
      // Refresh the punch list
      if (selectedVendorType) {
        loadPunchList(selectedVendorType);
      }
      
      setCostPrompt(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to submit cost:', error);
      alert('Failed to mark complete: ' + error.message);
    }
  };

  // Print punch list
  const printPunchList = (vendorType, list) => {
    const typeLabel = VENDOR_TYPES.find(v => v.value === vendorType)?.label || vendorType;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${typeLabel} Punch List</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
            h1 { font-size: 18px; margin-bottom: 5px; border-bottom: 2px solid #333; padding-bottom: 5px; }
            .date { font-size: 10px; color: #666; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 11px; }
            th { background: #f5f5f5; font-weight: bold; }
            tr:nth-child(even) { background: #fafafa; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <h1>${typeLabel} Punch List</h1>
          <p class="date">Generated: ${new Date().toLocaleString()}</p>
          <p style="margin-bottom: 10px;"><strong>${list.length}</strong> vehicle${list.length !== 1 ? 's' : ''} need this service</p>
          <table>
            <thead>
              <tr>
                <th>Stock #</th>
                <th>Vehicle</th>
                <th>Color</th>
                <th>VIN</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${list.map(v => `
                <tr>
                  <td><strong>${v.stockNumber || '-'}</strong></td>
                  <td>${v.year || ''} ${v.make || ''} ${v.model || ''}</td>
                  <td>${v.color || '-'}</td>
                  <td style="font-family: monospace; font-size: 10px;">${v.vin || '-'}</td>
                  <td>${v.vendorNotes || v.complaint || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px;">
            <p style="font-size: 10px; color: #666;">□ All items completed &nbsp;&nbsp;&nbsp; Signature: __________________ &nbsp;&nbsp;&nbsp; Date: __________</p>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };

  // Print Admin Task List (terminal-style)
  const printAdminTaskList = () => {
    const pendingTodos = adminTodos.filter(t => !t.done);
    if (pendingTodos.length === 0) { alert('No pending admin tasks.'); return; }
    // Group by vehicle
    const grouped = {};
    pendingTodos.forEach(t => {
      if (!grouped[t.vehicleId]) grouped[t.vehicleId] = [];
      grouped[t.vehicleId].push(t);
    });
    const vehicleRows = Object.entries(grouped).map(([vid, todos]) => {
      const v = workflowVehicles.find(x => x.id === parseInt(vid));
      const label = v ? `${v.stockNumber} - ${v.year} ${v.make} ${v.model}` : `Vehicle #${vid}`;
      const items = todos.map(t => `  [ ] ${t.text}`).join('\n');
      return `\n> ${label}\n${items}`;
    }).join('\n');

    const printContent = `<!DOCTYPE html><html><head><title>Admin Task List</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: #1a1a2e; color: #0ff; font-family: 'Courier New', monospace; padding: 30px; font-size: 13px; }
  h1 { color: #0f0; font-size: 18px; margin-bottom: 4px; }
  .date { color: #888; font-size: 11px; margin-bottom: 20px; }
  .vehicle { color: #ff0; font-size: 14px; font-weight: bold; margin-top: 16px; margin-bottom: 4px; }
  .todo { color: #0ff; margin-left: 16px; line-height: 1.8; }
  .checkbox { color: #0f0; }
  .footer { margin-top: 30px; border-top: 1px solid #333; padding-top: 10px; color: #666; font-size: 10px; }
  @media print { body { background: #fff; color: #000; } .vehicle { color: #333; } .todo { color: #000; } .checkbox { color: #000; } h1 { color: #000; } }
</style></head><body>
<h1>== ADMIN TASK LIST ==</h1>
<div class="date">Generated: ${new Date().toLocaleString()} | ${pendingTodos.length} tasks across ${Object.keys(grouped).length} vehicles</div>
${Object.entries(grouped).map(([vid, todos]) => {
      const v = workflowVehicles.find(x => x.id === parseInt(vid));
      const label = v ? `${v.stockNumber} - ${v.year} ${v.make} ${v.model}` : `Vehicle #${vid}`;
      return `<div class="vehicle">&gt; ${label}</div>${todos.map(t => `<div class="todo"><span class="checkbox">[ ]</span> ${t.text}</div>`).join('')}`;
    }).join('')}
<div class="footer">LotSense Admin Tasks | Printed ${new Date().toLocaleDateString()}</div>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(printContent); w.document.close(); w.onload = () => w.print(); }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-neutral-500">Loading workflow data...</div>
      </div>
    );
  }

  const totalActive = workflowVehicles.filter(v => !['shop_personal', 'copart', 'iaai', 'salvage'].includes(v.endUse)).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-neutral-800">Workflow Management</h1>
        <div className="flex gap-2">
          {/* QR Code Popup Button */}
          <div className="relative">
            <button
              onClick={() => setShowQRPopup(!showQRPopup)}
              className="px-3 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm hover:bg-neutral-200 flex items-center gap-1"
              title="QR Code for Run List"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </button>
            {showQRPopup && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowQRPopup(false)} />
                <div className="absolute right-0 mt-2 z-50 bg-white rounded-lg shadow-xl border border-neutral-200 p-4" style={{ minWidth: '200px' }}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-medium text-neutral-500">Scan for Run List</span>
                    <button onClick={() => setShowQRPopup(false)} className="text-neutral-400 hover:text-neutral-600">✕</button>
                  </div>
                  <div className="flex justify-center">
                    <QRCodeSVG value="https://lotsense.thewholesalecars.com/runlist" size={150} level="M" />
                  </div>
                  <div className="text-center mt-2 text-xs text-neutral-500">Weekly Run List</div>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-1">
            <a href="https://www.car-part.com" target="_blank" rel="noopener noreferrer" className="px-2 py-2 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600" title="Car-Part.com">Car-Part</a>
            <a href="https://www.ebay.com" target="_blank" rel="noopener noreferrer" className="px-2 py-2 bg-yellow-400 text-neutral-900 rounded-lg text-xs font-bold hover:bg-yellow-300" title="eBay">eBay</a>
            <a href="https://www.amazon.com" target="_blank" rel="noopener noreferrer" className="px-2 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700" title="Amazon">Amazon</a>
          </div>
          <button
            onClick={() => setShowRunList(true)}
            className="px-4 py-2 bg-neutral-800 text-white rounded-lg text-sm hover:bg-neutral-900 flex items-center gap-2"
          >
            📋 Run List {runList.length > 0 && <span className="bg-white text-neutral-800 px-1.5 py-0.5 rounded text-xs font-bold">{runList.length}</span>}
          </button>
          <button
            onClick={() => setShowWorkList(true)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 flex items-center gap-2"
          >
            🔧 Work List {workList.length > 0 && <span className="bg-white text-amber-700 px-1.5 py-0.5 rounded text-xs font-bold">{workList.length}</span>}
          </button>
          <button
            onClick={downloadTaskReport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
          >
            📥 Download Task Report
          </button>
          <button
            onClick={printAdminTaskList}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2"
          >
            📋 Admin Task List {adminTodos.filter(t => !t.done).length > 0 && <span className="bg-white text-blue-700 px-1.5 py-0.5 rounded text-xs font-bold">{adminTodos.filter(t => !t.done).length}</span>}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg flex items-center gap-2">📊 Workflow Summary</h2>
          <span className="text-2xl font-bold text-blue-600">{totalActive} vehicles</span>
        </div>
        <div className="grid grid-cols-5 gap-3 text-sm">
          {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([key, cat]) => {
            // For vendor_work, use the vendorNeeds count instead of status-based count
            const count = key === 'vendor_work' 
              ? vendorWorkCount 
              : cat.statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);
            return (
              <div key={key} className="bg-white rounded-lg p-3 text-center border">
                <div className="text-2xl font-bold text-neutral-800">{count}</div>
                <div className="text-xs text-neutral-500">{cat.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Vendor Needs Board */}
      {vendorNeeds.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <span className="text-lg">🔧</span> Work Type Summary
          </h3>
          <p className="text-xs text-neutral-500 mb-3">Click to see vehicles and print punch list</p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {vendorNeeds.map(vn => (
              <button
                key={vn.vendor_type}
                onClick={() => setSelectedVendorType(vn.vendor_type)}
                className="bg-white border border-neutral-200 rounded-lg p-3 text-left hover:bg-blue-50 transition shadow-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-neutral-800 text-sm truncate pr-2">
                    {VENDOR_TYPES.find(v => v.value === vn.vendor_type)?.label || vn.vendor_type}
                  </span>
                  <span className="bg-neutral-800 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                    {vn.count}
                  </span>
                </div>
                <div className="text-xs text-neutral-500">{vn.count} vehicle{vn.count !== 1 ? 's' : ''}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex items-center gap-2 border-b border-neutral-200">
        {Object.entries(CATEGORIES).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setCategoryTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              categoryTab === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {cat.label}
            {cat.statuses && (
              <span className="ml-1 text-xs bg-neutral-100 px-1.5 py-0.5 rounded">
                {cat.statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0)}
              </span>
            )}
          </button>
        ))}
        <div className="flex-1" />
        {(showOnlyStatuses.length > 0 || hiddenStatuses.length > 0) && (
          <button
            onClick={() => { setShowOnlyStatuses([]); setHiddenStatuses([]); }}
            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded flex items-center gap-1 hover:bg-red-200"
          >
            Clear Filters ({showOnlyStatuses.length + hiddenStatuses.length})
            <span className="ml-1">×</span>
          </button>
        )}
        <button
          onClick={() => setShowFilterPanel(!showFilterPanel)}
          className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
        >
          {showFilterPanel ? '▼' : '▶'} Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="bg-neutral-50 rounded-lg p-4 space-y-4">
          {/* Internal Statuses Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-neutral-700">Internal Statuses</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowOnlyStatuses(prev => [...new Set([...prev, ...INTERNAL_STATUSES.map(s => s.value)])])}
                  className="text-xs text-green-600 hover:underline"
                >Show All</button>
                <button 
                  onClick={() => setHiddenStatuses(prev => [...new Set([...prev, ...INTERNAL_STATUSES.map(s => s.value)])])}
                  className="text-xs text-red-600 hover:underline"
                >Hide All</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {INTERNAL_STATUSES.map(s => {
                const isShowOnly = showOnlyStatuses.includes(s.value);
                const isHidden = hiddenStatuses.includes(s.value);
                return (
                  <button
                    key={s.value}
                    onClick={() => {
                      if (isHidden) {
                        setHiddenStatuses(prev => prev.filter(v => v !== s.value));
                      } else if (isShowOnly) {
                        setShowOnlyStatuses(prev => prev.filter(v => v !== s.value));
                      } else {
                        setShowOnlyStatuses(prev => [...prev, s.value]);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (isHidden) {
                        setHiddenStatuses(prev => prev.filter(v => v !== s.value));
                      } else {
                        setHiddenStatuses(prev => [...prev, s.value]);
                        setShowOnlyStatuses(prev => prev.filter(v => v !== s.value));
                      }
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full transition ${
                      isShowOnly ? 'bg-green-600 text-white' :
                      isHidden ? 'bg-red-600 text-white line-through' :
                      'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-100'
                    }`}
                    title="Click to show only, right-click to hide"
                  >
                    {s.label}
                    <span className="ml-1 opacity-70">({statusCounts[s.value] || 0})</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Vendor Statuses Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-neutral-700">Vendor Statuses</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowOnlyStatuses(prev => [...new Set([...prev, ...VENDOR_WORKFLOW_STATUSES.map(s => s.value)])])}
                  className="text-xs text-green-600 hover:underline"
                >Show All</button>
                <button 
                  onClick={() => setHiddenStatuses(prev => [...new Set([...prev, ...VENDOR_WORKFLOW_STATUSES.map(s => s.value)])])}
                  className="text-xs text-red-600 hover:underline"
                >Hide All</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {VENDOR_WORKFLOW_STATUSES.map(s => {
                const isShowOnly = showOnlyStatuses.includes(s.value);
                const isHidden = hiddenStatuses.includes(s.value);
                return (
                  <button
                    key={s.value}
                    onClick={() => {
                      if (isHidden) {
                        setHiddenStatuses(prev => prev.filter(v => v !== s.value));
                      } else if (isShowOnly) {
                        setShowOnlyStatuses(prev => prev.filter(v => v !== s.value));
                      } else {
                        setShowOnlyStatuses(prev => [...prev, s.value]);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (isHidden) {
                        setHiddenStatuses(prev => prev.filter(v => v !== s.value));
                      } else {
                        setHiddenStatuses(prev => [...prev, s.value]);
                        setShowOnlyStatuses(prev => prev.filter(v => v !== s.value));
                      }
                    }}
                    className={`text-xs px-3 py-1.5 rounded-full transition ${
                      isShowOnly ? 'bg-green-600 text-white' :
                      isHidden ? 'bg-red-600 text-white line-through' :
                      'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-100'
                    }`}
                    title="Click to show only, right-click to hide"
                  >
                    {s.label}
                    <span className="ml-1 opacity-70">({statusCounts[s.value] || 0})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Clear All */}
          {(showOnlyStatuses.length > 0 || hiddenStatuses.length > 0) && (
            <div className="flex justify-end gap-3 pt-2 border-t border-neutral-200">
              {showOnlyStatuses.length > 0 && (
                <button onClick={() => setShowOnlyStatuses([])} className="text-xs text-blue-600 hover:underline">Clear Show Only</button>
              )}
              {hiddenStatuses.length > 0 && (
                <button onClick={() => setHiddenStatuses([])} className="text-xs text-blue-600 hover:underline">Clear Hidden</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search by stock #, VIN, make, model..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 text-sm border border-neutral-200 rounded-lg"
        />
        <span className="text-sm text-neutral-500">{filteredVehicles.length} vehicles</span>
      </div>

      {/* Vehicle list */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Stock #</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Vehicle</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Exit</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Status</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Location</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide min-w-[150px]">Notes</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wide">Files</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wide">RO</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wide">Parts</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wide">QR</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-neutral-500 uppercase tracking-wide">Todo</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide">Run List</th>
              <th className="px-3 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wide">Work</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {activeVehicles.length === 0 ? (
              <tr>
                <td colSpan="12" className="px-4 py-8 text-center text-neutral-500">
                  No vehicles match the current filters
                </td>
              </tr>
            ) : (
              activeVehicles.map(vehicle => (
                <tr key={vehicle.id} className="hover:bg-neutral-50">
                  {/* Stock # - clickable to open VehicleDetail */}
                  <td className="px-3 py-2">
                    <button
                      onClick={() => onSelectVehicle && onSelectVehicle(vehicle)}
                      className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {vehicle.stockNumber}
                    </button>
                  </td>
                  {/* Vehicle info - also clickable */}
                  <td className="px-3 py-2">
                    <button
                      onClick={() => onSelectVehicle && onSelectVehicle(vehicle)}
                      className="text-left hover:text-blue-600"
                    >
                      <div className="font-medium text-neutral-900">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                      <div className="text-xs text-neutral-500">{vehicle.color}</div>
                    </button>
                    {vehicle.vin && <VinDisplay vin={vehicle.vin} className="mt-0.5" />}
                  </td>
                  {/* Exit Strategy - inline dropdown */}
                  <td className="px-3 py-2">
                    <select
                      value={vehicle.endUse || 'retail'}
                      onChange={(e) => updateField(vehicle.id, 'endUse', e.target.value)}
                      disabled={savingField === `${vehicle.id}-endUse`}
                      className={`text-xs px-2 py-1 rounded border border-neutral-200 bg-white cursor-pointer ${
                        savingField === `${vehicle.id}-endUse` ? 'opacity-50' : ''
                      } ${
                        vehicle.endUse === 'dealer_direct' ? 'text-red-700 bg-red-50' :
                        vehicle.endUse === 'retail' ? 'text-green-700 bg-green-50' :
                        vehicle.endUse === 'wholesale' ? 'text-blue-700 bg-blue-50' : ''
                      }`}
                    >
                      <option value="retail">Retail</option>
                      <option value="wholesale">Wholesale</option>
                      <option value="dealer_direct">Byrider/DD</option>
                    </select>
                  </td>
                  {/* Workflow Status - multi-select with hover tooltip */}
                  <td className="px-3 py-2">
                    <div className="relative group">
                      <MultiStatusSelect
                        vehicle={vehicle}
                        statuses={vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'awaiting_inspection']}
                        onChange={(newStatuses) => updateField(vehicle.id, 'reconWorkflowStatuses', newStatuses)}
                        disabled={savingField === `${vehicle.id}-reconWorkflowStatuses`}
                      />
                      {/* Hover tooltip showing all statuses */}
                      {(vehicle.reconWorkflowStatuses?.length > 1 || (vehicle.reconWorkflowStatuses?.length === 1 && vehicle.reconWorkflowStatuses[0] !== 'awaiting_inspection')) && (
                        <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-neutral-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 max-w-[250px]">
                          {(vehicle.reconWorkflowStatuses || []).map(s => {
                            const status = WORKFLOW_STATUSES.find(ws => ws.value === s);
                            return status ? status.label : s;
                          }).join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Location - editable dropdown */}
                  <td className="px-3 py-2">
                    <select
                      value={vehicle.currentLocation || ''}
                      onChange={(e) => {
                        if (e.target.value === '__add__') {
                          setShowAddLocation(true);
                        } else {
                          updateField(vehicle.id, 'currentLocation', e.target.value);
                        }
                      }}
                      disabled={savingField === `${vehicle.id}-currentLocation`}
                      className={`text-xs px-2 py-1 rounded border border-neutral-200 bg-white cursor-pointer ${
                        savingField === `${vehicle.id}-currentLocation` ? 'opacity-50' : ''
                      } ${vehicle.currentLocation ? 'text-blue-700 bg-blue-50' : ''}`}
                    >
                      <option value="">On Lot</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.name}>{loc.name}</option>
                      ))}
                      <option value="__add__">+ Add Location</option>
                    </select>
                  </td>
                  {/* Notes - click to edit, hover for full text */}
                  <td className="px-3 py-2">
                    <div className="relative group">
                      <button
                        onClick={() => setEditingNotes({ vehicle, notes: vehicle.reconGamePlan || '' })}
                        className="text-left w-full"
                      >
                        {vehicle.reconGamePlan ? (
                          <div className="text-xs text-neutral-700 truncate max-w-[150px] group-hover:text-blue-600">
                            {vehicle.reconGamePlan.length > 30 ? vehicle.reconGamePlan.substring(0, 30) + '...' : vehicle.reconGamePlan}
                          </div>
                        ) : (
                          <span className="text-xs text-neutral-400 italic group-hover:text-blue-600">+ Add notes</span>
                        )}
                      </button>
                      {/* Hover tooltip showing full notes */}
                      {vehicle.reconGamePlan && vehicle.reconGamePlan.length > 30 && (
                        <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-neutral-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 max-w-[300px] whitespace-pre-wrap">
                          {vehicle.reconGamePlan}
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Files */}
                  <td className="px-3 py-2 text-center">
                    <VehicleFilesButton vehicleId={vehicle.id} vehicle={vehicle} className="text-xs" />
                  </td>
                  {/* RO */}
                  <td className="px-3 py-2 text-center">
                    {vehicle.hasOpenRO || vehicle.roId ? (
                      <button
                        onClick={() => {
                          if (vehicle.roId) {
                            localStorage.setItem('pendingROId', vehicle.roId);
                            window.open(window.location.origin + window.location.pathname + '#recon', '_blank');
                          }
                        }}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        View RO
                      </button>
                    ) : (
                      <button
                        onClick={() => handleCreateRO(vehicle)}
                        disabled={creatingRO === vehicle.id}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                      >
                        {creatingRO === vehicle.id ? '...' : '+ RO'}
                      </button>
                    )}
                  </td>
                  {/* Parts Status */}
                  <td className="px-3 py-2 text-center">
                    {(vehicle.partsOrdered > 0 || vehicle.partsArrived > 0) ? (
                      <div className="relative group inline-block">
                        <button
                          onClick={() => {
                            window.open(window.location.origin + window.location.pathname + '#partsroom', '_blank');
                          }}
                          className="text-lg cursor-pointer hover:scale-125 transition-transform"
                          title="Click to open Parts Room"
                        >
                          {vehicle.partsOrdered > 0 ? '🟡' : '🟢'}
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-neutral-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-[200px]">
                          {vehicle.partsDetails && vehicle.partsDetails.length > 0 ? (
                            vehicle.partsDetails.map((part, idx) => (
                              <div key={idx} className="whitespace-nowrap">
                                {part.name || 'Part'} - {part.status === 'ordered' ? '⏳ ordered' : '✅ here'}
                              </div>
                            ))
                          ) : (
                            <>
                              {vehicle.partsOrdered > 0 && <div>{vehicle.partsOrdered} part{vehicle.partsOrdered > 1 ? 's' : ''} pending</div>}
                              {vehicle.partsArrived > 0 && <div>{vehicle.partsArrived} part{vehicle.partsArrived > 1 ? 's' : ''} here</div>}
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-neutral-300">—</span>
                    )}
                  </td>
                  {/* QR Code */}
                  <td className="px-3 py-2 text-center">
                    <QRCodePopup vehicle={vehicle} />
                  </td>
                  {/* Admin Todo */}
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => setTodoVehicle(vehicle)}
                      className="relative text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded hover:bg-neutral-200"
                      title="Admin tasks"
                    >
                      📋
                      {getTodoCount(vehicle.id) > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{getTodoCount(vehicle.id)}</span>
                      )}
                    </button>
                  </td>
                  {/* Run List - add button */}
                  <td className="px-3 py-2 text-right">
                    {runList.some(v => v.id === vehicle.id) ? (
                      <span className="text-xs text-green-600 font-medium">✓ Added</span>
                    ) : (
                      <button
                        onClick={() => setAddToListVehicle(vehicle)}
                        className="text-xs px-2 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-700"
                      >
                        + Add
                      </button>
                    )}
                  </td>
                  {/* Work List - add button */}
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setAddToWorkListVehicle(vehicle)}
                      className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700"
                      title="Add task to Work List"
                    >
                      🔧+
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Ready for Sale - Collapsible Section */}
      {readyVehiclesAll.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setReadyExpanded(!readyExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between bg-green-50 hover:bg-green-100 transition"
          >
            <div className="flex items-center gap-2">
              <span className="text-green-700 font-medium">Ready for Sale</span>
              <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">{readyVehiclesAll.length}</span>
            </div>
            <span className="text-green-600">{readyExpanded ? '▼' : '▶'}</span>
          </button>
          {readyExpanded && (
            <div>
              <div className="px-4 py-2 bg-white border-b flex items-center gap-2">
                <span className="text-xs text-neutral-500 mr-1">CR Filter:</span>
                <button onClick={() => setCrFilter('all')} className={`text-xs px-2 py-1 rounded ${crFilter === 'all' ? 'bg-neutral-800 text-white' : 'bg-white border text-neutral-600 hover:bg-neutral-50'}`}>All</button>
                <button onClick={() => setCrFilter('missing')} className={`text-xs px-2 py-1 rounded ${crFilter === 'missing' ? 'bg-red-600 text-white' : 'bg-white border border-red-200 text-red-600 hover:bg-red-50'}`}>Missing CR ({readyVehiclesAll.filter(v => !v.crOpenlane || !v.crAcv).length})</button>
                <button onClick={() => setCrFilter('complete')} className={`text-xs px-2 py-1 rounded ${crFilter === 'complete' ? 'bg-green-600 text-white' : 'bg-white border border-green-200 text-green-600 hover:bg-green-50'}`}>CR Complete ({readyVehiclesAll.filter(v => v.crOpenlane && v.crAcv).length})</button>
              </div>
              <div className="max-h-[40vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Stock #</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Vehicle</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-neutral-500 uppercase">OpenLane CR</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-neutral-500 uppercase">ACV CR</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Location</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Notes</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-neutral-500 uppercase">Files</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-neutral-500 uppercase">QR</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-neutral-500 uppercase">Run List</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-neutral-500 uppercase">Work</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {readyVehicles.map(vehicle => (
                    <tr key={vehicle.id} className="hover:bg-green-50">
                      <td className="px-3 py-2">
                        <button
                          onClick={() => onSelectVehicle && onSelectVehicle(vehicle)}
                          className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {vehicle.stockNumber}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-neutral-900">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                        <div className="text-xs text-neutral-500">{vehicle.color}</div>
                      </td>
                      <td className="px-3 py-2">
                        <MultiStatusSelect
                          vehicle={vehicle}
                          statuses={vehicle.reconWorkflowStatuses || [vehicle.reconWorkflowStatus || 'ready_for_sale']}
                          onChange={(newStatuses) => updateField(vehicle.id, 'reconWorkflowStatuses', newStatuses)}
                          disabled={savingField === `${vehicle.id}-reconWorkflowStatuses`}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!vehicle.crOpenlane}
                          onChange={(e) => updateField(vehicle.id, 'crOpenlane', e.target.checked)}
                          disabled={savingField === `${vehicle.id}-crOpenlane`}
                          className="w-4 h-4 accent-green-600 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!vehicle.crAcv}
                          onChange={(e) => updateField(vehicle.id, 'crAcv', e.target.checked)}
                          disabled={savingField === `${vehicle.id}-crAcv`}
                          className="w-4 h-4 accent-green-600 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={vehicle.currentLocation || ''}
                          onChange={(e) => {
                            if (e.target.value === '__add__') {
                              setShowAddLocation(true);
                            } else {
                              updateField(vehicle.id, 'currentLocation', e.target.value);
                            }
                          }}
                          className={`text-xs border border-neutral-200 rounded px-2 py-1 ${
                            savingField === `${vehicle.id}-currentLocation` ? 'opacity-50' : ''
                          } ${vehicle.currentLocation ? 'text-blue-700 bg-blue-50' : ''}`}
                        >
                          <option value="">On Lot</option>
                          {locations.map(loc => (
                            <option key={loc.id} value={loc.name}>{loc.name}</option>
                          ))}
                          <option value="__add__">+ Add Location</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => setEditingNotes({ vehicle, notes: vehicle.reconGamePlan || '' })}
                          className="text-left w-full group"
                          title={vehicle.reconGamePlan || 'Click to add notes'}
                        >
                          {vehicle.reconGamePlan ? (
                            <div className="text-xs text-neutral-700 truncate max-w-[150px] group-hover:text-blue-600">
                              {vehicle.reconGamePlan.length > 30 ? vehicle.reconGamePlan.substring(0, 30) + '...' : vehicle.reconGamePlan}
                            </div>
                          ) : (
                            <span className="text-xs text-neutral-400 italic group-hover:text-blue-600">+ Add notes</span>
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <VehicleFilesButton vehicleId={vehicle.id} vehicle={vehicle} className="text-xs" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <QRCodePopup vehicle={vehicle} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {runList.some(v => v.id === vehicle.id) ? (
                          <span className="text-xs text-green-600 font-medium">Added</span>
                        ) : (
                          <button
                            onClick={() => setAddToListVehicle(vehicle)}
                            className="text-xs px-2 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-700"
                          >
                            + Add
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => setAddToWorkListVehicle(vehicle)}
                          className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700"
                          title="Add task to Work List"
                        >
                          🔧+
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
      )}

      
      {/* Punch List Modal */}
      {selectedVendorType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedVendorType(null)}>
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="font-semibold text-lg">{VENDOR_TYPES.find(v => v.value === selectedVendorType)?.label || selectedVendorType}</h2>
                <p className="text-sm text-neutral-500">{loadingPunchList ? 'Loading...' : `${punchList.length} vehicle${punchList.length !== 1 ? 's' : ''}`}</p>
              </div>
              <div className="flex gap-2 items-center">
                {paymentBatch.length > 0 && (
                  <button 
                    onClick={() => setShowBatchModal(true)} 
                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                  >
                    📦 Batch ({paymentBatch.length})
                  </button>
                )}
                <button onClick={() => printPunchList(selectedVendorType, punchList)} disabled={loadingPunchList || punchList.length === 0} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">🖨️ Print</button>
                <button onClick={() => setSelectedVendorType(null)} className="text-neutral-400 hover:text-neutral-600 text-2xl px-2">×</button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {loadingPunchList ? (
                <div className="text-center py-8 text-neutral-400">Loading...</div>
              ) : punchList.length === 0 ? (
                <div className="text-center py-8 text-neutral-400">No vehicles need this service</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Stock #</th>
                      <th className="px-3 py-2 text-left">Vehicle</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {punchList.map(v => (
                      <tr key={v.id} className="hover:bg-neutral-50">
                        <td className="px-3 py-2 font-mono">{v.stockNumber}</td>
                        <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                        <td className="px-3 py-2 text-xs">{v.vendorNotes || v.complaint || '-'}</td>
                        <td className="px-3 py-2 text-right flex gap-2 justify-end">
                          {paymentBatch.some(b => b.vehicle.id === v.id) ? (
                            <span className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded">In Batch</span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Only add if not already in batch
                                setPaymentBatch(prev => {
                                  if (prev.some(b => b.vehicle.id === v.id)) return prev;
                                  return [...prev, { vehicle: v, vendorType: selectedVendorType, amount: '' }];
                                });
                              }}
                              className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              + Batch
                            </button>
                          )}
                          <button
                            onClick={() => setCostPrompt({ vehicle: v, vendorType: selectedVendorType })}
                            className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            ✓ Complete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Prompt Modal */}
      {costPrompt && (
        <CostPromptModal
          vehicle={costPrompt.vehicle}
          vendorType={costPrompt.vendorType}
          onSubmit={handleCostSubmit}
          onClose={() => setCostPrompt(null)}
        />
      )}

      {/* Payment Batch Modal */}
      {showBatchModal && paymentBatch.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowBatchModal(false)}>
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="font-semibold text-lg">Payment Batch</h2>
                <p className="text-sm text-neutral-500">{paymentBatch.length} vehicle{paymentBatch.length !== 1 ? 's' : ''} • {VENDOR_TYPES.find(v => v.value === paymentBatch[0]?.vendorType)?.label || ''}</p>
              </div>
              <button onClick={() => setShowBatchModal(false)} className="text-neutral-400 hover:text-neutral-600 text-2xl px-2">×</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <table className="w-full text-sm mb-4">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Stock #</th>
                    <th className="px-3 py-2 text-left">Vehicle</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-center">Remove</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paymentBatch.map((item, idx) => (
                    <tr key={item.vehicle.id} className="hover:bg-neutral-50">
                      <td className="px-3 py-2 font-mono">{item.vehicle.stockNumber}</td>
                      <td className="px-3 py-2">{item.vehicle.year} {item.vehicle.make} {item.vehicle.model}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={item.amount}
                          onChange={(e) => {
                            const newBatch = [...paymentBatch];
                            newBatch[idx].amount = e.target.value;
                            setPaymentBatch(newBatch);
                          }}
                          placeholder="0.00"
                          className="w-24 px-2 py-1 border border-neutral-300 rounded text-right text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => setPaymentBatch(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700"
                        >×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-neutral-100 font-semibold">
                  <tr>
                    <td colSpan="2" className="px-3 py-2 text-right">Total:</td>
                    <td className="px-3 py-2 text-right">
                      ${paymentBatch.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="p-4 border-t flex justify-between items-center bg-neutral-50">
              <button
                onClick={() => setPaymentBatch([])}
                className="text-sm text-red-600 hover:underline"
              >
                Clear All
              </button>
              <button
                onClick={async () => {
                  // Process all batch items - mark each vendor complete
                  for (const item of paymentBatch) {
                    await markVendorComplete(item.vehicle, item.vendorType, item.amount);
                  }
                  // Print combined receipt
                  const vendorLabel = VENDOR_TYPES.find(v => v.value === paymentBatch[0]?.vendorType)?.label || 'Vendor';
                  const total = paymentBatch.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                  const printContent = `
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <title>Payment Receipt - ${vendorLabel}</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
                          h1 { font-size: 18px; margin-bottom: 5px; border-bottom: 2px solid #333; padding-bottom: 5px; }
                          .date { font-size: 10px; color: #666; margin-bottom: 15px; }
                          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 11px; }
                          th { background: #f5f5f5; font-weight: bold; }
                          .total { font-weight: bold; background: #f0f0f0; }
                          @media print { body { padding: 10px; } }
                        </style>
                      </head>
                      <body>
                        <h1>Payment Receipt: ${vendorLabel}</h1>
                        <div class="date">Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
                        <table>
                          <thead>
                            <tr>
                              <th>Stock #</th>
                              <th>Vehicle</th>
                              <th style="text-align: right;">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${paymentBatch.map(item => `
                              <tr>
                                <td>${item.vehicle.stockNumber}</td>
                                <td>${item.vehicle.year} ${item.vehicle.make} ${item.vehicle.model}</td>
                                <td style="text-align: right;">$${(parseFloat(item.amount) || 0).toFixed(2)}</td>
                              </tr>
                            `).join('')}
                          </tbody>
                          <tfoot>
                            <tr class="total">
                              <td colspan="2" style="text-align: right;">TOTAL:</td>
                              <td style="text-align: right;">$${total.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </body>
                    </html>
                  `;
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(printContent);
                  printWindow.document.close();
                  printWindow.print();
                  // Clear batch and close
                  setPaymentBatch([]);
                  setShowBatchModal(false);
                  // Refresh punch list
                  if (selectedVendorType) loadPunchList(selectedVendorType);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                🖨️ Print Receipt & Complete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      {showAddLocation && (
        <AddLocationModal
          onSave={addLocation}
          onClose={() => setShowAddLocation(false)}
        />
      )}

      {/* Add to Run List Modal */}
      {addToListVehicle && !showAddDestination && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAddToListVehicle(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Add to Run List</h3>
            <p className="text-sm text-neutral-500 mb-4">
              {addToListVehicle.stockNumber} - {addToListVehicle.year} {addToListVehicle.make} {addToListVehicle.model}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">Select Destination</label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {allDestinations.map(dest => (
                  <button
                    key={dest.value}
                    onClick={() => addToRunList(addToListVehicle, dest.value)}
                    className="w-full px-4 py-3 text-left border border-neutral-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition"
                  >
                    <span className="font-medium">{dest.label}</span>
                  </button>
                ))}
                <button
                  onClick={() => setShowAddDestination(true)}
                  className="w-full px-4 py-3 text-left border border-dashed border-neutral-300 rounded-lg hover:bg-neutral-50 text-neutral-600"
                >
                  <span className="font-medium">+ Add New Destination</span>
                </button>
              </div>
            </div>
            <button
              onClick={() => setAddToListVehicle(null)}
              className="w-full px-4 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Destination Modal */}
      {showAddDestination && (
        <AddDestinationModal
          onSave={addCustomDestination}
          onClose={() => setShowAddDestination(false)}
        />
      )}

      {/* Add to Work List Modal */}
      {addToWorkListVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setAddToWorkListVehicle(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Add to Work List</h3>
            <p className="text-sm text-neutral-500 mb-4">
              {addToWorkListVehicle.stockNumber} - {addToWorkListVehicle.year} {addToWorkListVehicle.make} {addToWorkListVehicle.model}
            </p>
            <div className="mb-3">
              <label className="block text-sm font-medium text-neutral-700 mb-1">What needs done?</label>
              <input
                type="text"
                id="worklistTaskInput"
                placeholder="e.g. Replace front bumper, Oil change..."
                className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    const catSelect = document.getElementById('worklistCatSelect');
                    setWorkList(prev => [...prev, {
                      id: Date.now(),
                      text: e.target.value.trim(),
                      category: catSelect?.value || 'unassigned',
                      vehicleId: addToWorkListVehicle.id,
                      vehicleLabel: `${addToWorkListVehicle.stockNumber} - ${addToWorkListVehicle.year} ${addToWorkListVehicle.make} ${addToWorkListVehicle.model}`,
                      notes: '',
                      done: false,
                      createdAt: new Date().toISOString()
                    }]);
                    setAddToWorkListVehicle(null);
                  }
                }}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
              <select id="worklistCatSelect" className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500">
                <option value="unassigned">Unassigned</option>
                {WORKLIST_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const taskInput = document.getElementById('worklistTaskInput');
                  const catSelect = document.getElementById('worklistCatSelect');
                  if (!taskInput?.value.trim()) return;
                  setWorkList(prev => [...prev, {
                    id: Date.now(),
                    text: taskInput.value.trim(),
                    category: catSelect?.value || 'unassigned',
                    vehicleId: addToWorkListVehicle.id,
                    vehicleLabel: `${addToWorkListVehicle.stockNumber} - ${addToWorkListVehicle.year} ${addToWorkListVehicle.make} ${addToWorkListVehicle.model}`,
                    notes: '',
                    done: false,
                    createdAt: new Date().toISOString()
                  }]);
                  setAddToWorkListVehicle(null);
                }}
                className="flex-1 px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 font-medium"
              >
                Add to Work List
              </button>
              <button
                onClick={() => setAddToWorkListVehicle(null)}
                className="px-4 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Run List View Modal */}
      {showRunList && (
        <RunListModal
          runList={runList}
          onRemove={removeFromRunList}
          onClear={clearRunList}
          onUpdate={updateRunListField}
          liveVehicles={workflowVehicles}
          onClose={() => setShowRunList(false)}
          showFinancialsOption={currentUser?.role === 'admin'}
          customDestinations={customDestinations}
        />
      )}

      {/* Work List Modal */}
      {showWorkList && (
        <WorkListModal
          workList={workList}
          onAdd={(item) => setWorkList(prev => [...prev, { id: Date.now(), ...item, done: false, createdAt: new Date().toISOString() }])}
          onRemove={(id) => setWorkList(prev => prev.filter(w => w.id !== id))}
          onToggle={(id) => setWorkList(prev => prev.map(w => w.id === id ? { ...w, done: !w.done } : w))}
          onUpdate={(id, field, value) => setWorkList(prev => prev.map(w => w.id === id ? { ...w, [field]: value } : w))}
          onClear={() => { if (window.confirm('Clear the entire work list?')) setWorkList([]); }}
          onClose={() => setShowWorkList(false)}
          vehicles={workflowVehicles}
        />
      )}

      {/* Edit Notes Modal */}
      {editingNotes && (
        <EditNotesModal
          vehicle={editingNotes.vehicle}
          initialNotes={editingNotes.notes}
          onSave={async (newNotes) => {
            await updateField(editingNotes.vehicle.id, 'reconGamePlan', newNotes);
            setEditingNotes(null);
          }}
          onClose={() => setEditingNotes(null)}
        />
      )}
    </div>
  );
};

// Cost Prompt Modal Component
const CostPromptModal = ({ vehicle, vendorType, onSubmit, onClose }) => {
  const [actualCost, setActualCost] = useState('');
  const [dontKnow, setDontKnow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(false);

  const handlePrintReceipt = () => {
    const cost = parseFloat(actualCost) || 0;
    const typeLabel = VENDOR_TYPES.find(v => v.value === vendorType)?.label || vendorType;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 400px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
            .header h1 { font-size: 20px; margin-bottom: 5px; }
            .header p { font-size: 12px; color: #666; }
            .details { margin-bottom: 20px; }
            .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            .row:last-child { border-bottom: none; }
            .label { color: #666; font-size: 12px; }
            .value { font-weight: bold; font-size: 14px; }
            .total { margin-top: 20px; padding-top: 15px; border-top: 2px solid #333; }
            .total .row { border-bottom: none; }
            .total .value { font-size: 24px; color: #16a34a; }
            .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PAYMENT RECEIPT</h1>
            <p>Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
          </div>
          <div class="details">
            <div class="row">
              <span class="label">Stock #</span>
              <span class="value">${vehicle.stockNumber}</span>
            </div>
            <div class="row">
              <span class="label">Vehicle</span>
              <span class="value">${vehicle.year} ${vehicle.make} ${vehicle.model}</span>
            </div>
            <div class="row">
              <span class="label">Service Type</span>
              <span class="value">${typeLabel}</span>
            </div>
          </div>
          <div class="total">
            <div class="row">
              <span class="label" style="font-size: 14px;">AMOUNT PAID</span>
              <span class="value">$${cost.toFixed(2)}</span>
            </div>
          </div>
          <div class="footer">
            <p>Thank you for your business!</p>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (printReceipt && actualCost) {
      handlePrintReceipt();
    }
    await onSubmit(dontKnow ? null : parseFloat(actualCost) || 0, dontKnow);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-2">Mark Complete & Enter Cost</h3>
        <p className="text-sm text-neutral-500 mb-4">
          {vehicle.stockNumber} - {vehicle.year} {vehicle.make} {vehicle.model}
          <br />
          <span className="font-medium">{VENDOR_TYPES.find(v => v.value === vendorType)?.label || vendorType}</span> work completed
        </p>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Actual Cost</label>
            <input
              type="number"
              step="0.01"
              value={actualCost}
              onChange={(e) => setActualCost(e.target.value)}
              disabled={dontKnow}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-lg disabled:bg-neutral-100"
            />
          </div>
          <label className="flex items-center gap-2 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={printReceipt}
              onChange={(e) => setPrintReceipt(e.target.checked)}
              disabled={dontKnow || !actualCost}
              className="rounded"
            />
            <span className="text-sm text-neutral-600">🖨️ Print payment receipt</span>
          </label>
          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={dontKnow}
              onChange={(e) => setDontKnow(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-neutral-600">I don't know - create admin reminder</span>
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (!dontKnow && !actualCost)}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : '✓ Complete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Location Modal Component
const AddLocationModal = ({ onSave, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    address: '',
    phone: '',
    contact: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">Add New Location</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Joe's Body Shop"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Address</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="123 Main St, City, ST"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Contact Person</label>
            <input
              type="text"
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="John Doe"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm resize-none"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!form.name.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Add Location
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Destination Modal Component
const AddDestinationModal = ({ onSave, onClose }) => {
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim());
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">Add New Destination</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Destination Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Manheim Ohio"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm"
              autoFocus
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Add Destination
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Work List Categories
const WORKLIST_CATEGORIES = [
  { value: 'mech_shop', label: 'Mech Shop', color: 'bg-blue-600' },
  { value: 'body_work', label: 'Body Work', color: 'bg-purple-600' },
  { value: 'detail', label: 'Detail', color: 'bg-pink-600' },
  { value: 'parts', label: 'Parts', color: 'bg-cyan-600' },
  { value: 'office', label: 'Office', color: 'bg-yellow-600' },
  { value: 'chris', label: 'Chris', color: 'bg-orange-600' },
];

// Work List Modal Component - Terminal Style (mirrors Run List)
const WorkListModal = ({ workList, onAdd, onRemove, onToggle, onUpdate, onClear, onClose, vehicles = [] }) => {
  const [newTask, setNewTask] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newVehicleId, setNewVehicleId] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'done'

  const handleAdd = () => {
    if (!newTask.trim()) return;
    const vehicle = newVehicleId ? vehicles.find(v => v.id === parseInt(newVehicleId)) : null;
    onAdd({
      text: newTask.trim(),
      category: newCategory || 'unassigned',
      vehicleId: vehicle ? vehicle.id : null,
      vehicleLabel: vehicle ? `${vehicle.stockNumber} - ${vehicle.year} ${vehicle.make} ${vehicle.model}` : null,
      notes: '',
    });
    setNewTask('');
    setNewVehicleId('');
  };

  const filtered = workList.filter(w => {
    if (filter === 'pending') return !w.done;
    if (filter === 'done') return w.done;
    return true;
  });

  const grouped = [
    ...WORKLIST_CATEGORIES.map(cat => ({
      ...cat,
      items: filtered.filter(w => w.category === cat.value)
    })),
    { value: 'unassigned', label: 'Unassigned', color: 'bg-neutral-600', items: filtered.filter(w => !w.category || w.category === 'unassigned' || !WORKLIST_CATEGORIES.some(c => c.value === w.category)) }
  ].filter(g => g.items.length > 0);

  const pendingCount = workList.filter(w => !w.done).length;
  const doneCount = workList.filter(w => w.done).length;

  const printWorkList = () => {
    const pendingItems = workList.filter(w => !w.done);
    const groupedPrint = [
      ...WORKLIST_CATEGORIES.map(cat => ({
        ...cat,
        items: pendingItems.filter(w => w.category === cat.value)
      })),
      { value: 'unassigned', label: 'Unassigned', items: pendingItems.filter(w => !w.category || w.category === 'unassigned' || !WORKLIST_CATEGORIES.some(c => c.value === w.category)) }
    ].filter(g => g.items.length > 0);

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Work List</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              background: #fff; 
              color: #000; 
              padding: 20px;
              font-size: 12px;
              line-height: 1.5;
            }
            .container { 
              border: 3px solid #000; 
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 { 
              font-size: 28px; 
              text-align: center; 
              margin-bottom: 5px;
              color: #000;
              font-weight: 900;
              letter-spacing: 2px;
            }
            .subtitle {
              text-align: center;
              color: #333;
              margin-bottom: 20px;
              font-size: 11px;
              font-weight: bold;
            }
            .section { 
              margin: 20px 0;
              border-top: 2px solid #000;
              padding-top: 15px;
            }
            .section-header { 
              font-size: 18px; 
              font-weight: 900;
              color: #000;
              padding: 5px 0;
              margin-bottom: 10px;
              border-bottom: 2px solid #000;
              text-transform: uppercase;
            }
            .task-row {
              padding: 8px 0;
              border-bottom: 1px solid #999;
            }
            .task-text { color: #000; font-weight: 900; font-size: 13px; }
            .task-vehicle { color: #000; font-weight: bold; }
            .task-notes { color: #222; font-style: italic; }
            .checkbox { display: inline-block; width: 14px; height: 14px; border: 2px solid #000; margin-right: 8px; vertical-align: middle; }
            .summary {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 3px solid #000;
              color: #000;
              font-weight: bold;
            }
            .summary-line { margin: 5px 0; }
            @media print { 
              body { background: #fff !important; color: #000 !important; } 
              .task-text, .task-vehicle, .section-header, h1, .summary { color: #000 !important; font-weight: 900 !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>WORK LIST</h1>
            <div class="subtitle">LOTSENSE DMS • Generated: ${new Date().toLocaleDateString()} • ${pendingItems.length} pending tasks</div>
            ${groupedPrint.map(group => `
              <div class="section">
                <div class="section-header">${group.label.toUpperCase()} (${group.items.length})</div>
                ${group.items.map(w => `
                  <div class="task-row">
                    <span class="checkbox"></span>
                    <span class="task-text">${w.text}</span>
                    ${w.vehicleLabel ? `<br/><span class="task-vehicle">VEHICLE: ${w.vehicleLabel}</span>` : ''}
                    ${w.notes ? `<br/><span class="task-notes">NOTE: ${w.notes}</span>` : ''}
                  </div>
                `).join('')}
              </div>
            `).join('')}
            <div class="summary">
              <div class="summary-line">${'═'.repeat(45)}</div>
              <div class="summary-line">TOTAL PENDING: ${pendingItems.length} tasks</div>
              ${WORKLIST_CATEGORIES.map(c => {
                const count = pendingItems.filter(w => w.category === c.value).length;
                return count > 0 ? `<div class="summary-line">${c.label}: ${count}</div>` : '';
              }).join('')}
            </div>
          </div>
        </body>
      </html>
    `;
    const w = window.open('', '_blank');
    if (w) { w.document.write(printContent); w.document.close(); w.onload = () => w.print(); }
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-amber-500">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-white font-mono">🔧 WORK LIST</h2>
          <div className="flex bg-neutral-800 rounded overflow-hidden">
            <button onClick={() => setFilter('all')} className={`px-3 py-1 text-xs font-mono font-bold ${filter === 'all' ? 'bg-amber-600 text-black' : 'text-neutral-400 hover:text-white'}`}>ALL ({workList.length})</button>
            <button onClick={() => setFilter('pending')} className={`px-3 py-1 text-xs font-mono font-bold ${filter === 'pending' ? 'bg-amber-600 text-black' : 'text-neutral-400 hover:text-white'}`}>PENDING ({pendingCount})</button>
            <button onClick={() => setFilter('done')} className={`px-3 py-1 text-xs font-mono font-bold ${filter === 'done' ? 'bg-amber-600 text-black' : 'text-neutral-400 hover:text-white'}`}>DONE ({doneCount})</button>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={printWorkList} disabled={pendingCount === 0} className="px-4 py-2 bg-amber-600 text-black font-mono text-sm font-bold rounded hover:bg-amber-500 disabled:opacity-50">🖨️ PRINT</button>
          <button onClick={onClear} disabled={workList.length === 0} className="px-4 py-2 bg-red-600 text-white font-mono text-sm font-bold rounded hover:bg-red-500 disabled:opacity-50">CLEAR ALL</button>
          <button onClick={onClose} className="px-4 py-2 bg-neutral-700 text-white font-mono text-sm font-bold rounded hover:bg-neutral-600">CLOSE</button>
        </div>
      </div>

      {/* Add Task Bar */}
      <div className="p-4 border-b border-neutral-800 bg-neutral-900">
        <div className="flex gap-2 max-w-4xl mx-auto">
          <input
            type="text"
            value={newTask}
            onChange={e => setNewTask(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="What needs to be done..."
            className="flex-1 bg-black border border-amber-700 text-amber-300 font-mono text-sm px-3 py-2 rounded focus:outline-none focus:border-amber-400"
            autoFocus
          />
          <select
            value={newCategory}
            onChange={e => setNewCategory(e.target.value)}
            className="bg-black border border-amber-700 text-amber-300 font-mono text-xs px-2 py-2 rounded"
          >
            <option value="">Category...</option>
            {WORKLIST_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select
            value={newVehicleId}
            onChange={e => setNewVehicleId(e.target.value)}
            className="bg-black border border-amber-700 text-amber-300 font-mono text-xs px-2 py-2 rounded max-w-[200px]"
          >
            <option value="">Vehicle (optional)...</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>)}
          </select>
          <button onClick={handleAdd} disabled={!newTask.trim()} className="px-4 py-2 bg-amber-600 text-black font-mono text-sm font-bold rounded hover:bg-amber-500 disabled:opacity-50">+ ADD</button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 font-mono text-sm">
        {filtered.length === 0 ? (
          <div className="text-center text-amber-500 py-20">
            <div className="text-2xl mb-2">[ EMPTY ]</div>
            <div className="text-neutral-500">{filter === 'done' ? 'No completed tasks yet.' : 'No tasks in work list. Add one above.'}</div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="text-neutral-500 mb-4">LOTSENSE DMS • {new Date().toLocaleDateString()} • {pendingCount} pending / {doneCount} done</div>
            {grouped.map(group => (
              <div key={group.value} className="mb-8">
                <div className="text-amber-400 text-xl font-bold mb-3 border-b border-amber-500 pb-1">
                  {group.label.toUpperCase()}
                  <span className="text-neutral-500 text-sm ml-2">({group.items.length})</span>
                </div>
                <div className="border-l-2 border-amber-800 pl-4">
                  {group.items.map(w => (
                    <div key={w.id} className={`py-3 border-b border-neutral-800 flex justify-between items-start ${w.done ? 'opacity-50' : ''}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={w.done}
                            onChange={() => onToggle(w.id)}
                            className="rounded text-amber-600 flex-shrink-0"
                          />
                          <span className={`text-white font-bold ${w.done ? 'line-through text-neutral-500' : ''}`}>{w.text}</span>
                        </div>
                        {w.vehicleLabel && <div className="text-cyan-400 text-xs ml-6">VEHICLE: {w.vehicleLabel}</div>}
                        <div className="mt-1.5 ml-6 flex flex-wrap gap-1.5 items-center">
                          <span className="text-neutral-600 text-xs mr-1">ASSIGN:</span>
                          {WORKLIST_CATEGORIES.map(cat => (
                            <button
                              key={cat.value}
                              onClick={() => onUpdate(w.id, 'category', w.category === cat.value ? 'unassigned' : cat.value)}
                              className={`px-2 py-0.5 rounded text-xs font-bold font-mono transition ${w.category === cat.value ? cat.color + ' text-white' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}
                            >
                              {cat.label}
                            </button>
                          ))}
                        </div>
                        <div className="mt-1.5 ml-6">
                          <input
                            type="text"
                            value={w.notes || ''}
                            onChange={e => onUpdate(w.id, 'notes', e.target.value)}
                            placeholder="Notes..."
                            className="w-full bg-neutral-900 border border-amber-800 text-amber-300 font-mono text-xs px-2 py-1 rounded focus:outline-none focus:border-amber-400"
                          />
                        </div>
                      </div>
                      <button onClick={() => onRemove(w.id)} className="text-red-500 hover:text-red-400 text-xs ml-2">[REMOVE]</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Summary */}
            <div className="border-t-2 border-amber-500 pt-4 mt-8">
              <div className="text-white font-bold mb-2">{'═'.repeat(45)}</div>
              <div className="text-white font-bold">SUMMARY</div>
              <div className="text-amber-400">Pending: {pendingCount} | Done: {doneCount} | Total: {workList.length}</div>
              {WORKLIST_CATEGORIES.map(c => {
                const count = filtered.filter(w => w.category === c.value).length;
                return count > 0 ? <div key={c.value} className="text-amber-400">{c.label}: {count}</div> : null;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Run List Categories
const RUNLIST_CATEGORIES = [
  { value: 'mech_shop', label: 'Mech Shop', color: 'bg-blue-600' },
  { value: 'outside_repairs', label: 'Outside Repairs', color: 'bg-purple-600' },
  { value: 'detail', label: 'Detail', color: 'bg-pink-600' },
  { value: 'chris', label: 'Chris', color: 'bg-orange-600' },
  { value: 'done', label: 'Done', color: 'bg-green-600' },
];

// Run List Modal Component - Terminal Style
const RunListModal = ({ runList, onRemove, onClear, onClose, onUpdate, liveVehicles = [], showFinancialsOption = false, customDestinations = [] }) => {
  const [showFinancials, setShowFinancials] = useState(false);
  const [viewMode, setViewMode] = useState('category'); // 'category' or 'master'

  // Sync statuses from live vehicle data
  const syncedRunList = runList.map(item => {
    const live = liveVehicles.find(v => v.id === item.id);
    if (!live) return item;
    const statuses = live.reconWorkflowStatuses && live.reconWorkflowStatuses.length > 0
      ? live.reconWorkflowStatuses
      : live.reconWorkflowStatus ? [live.reconWorkflowStatus] : item.needs;
    return { ...item, needs: statuses, location: live.currentLocation || item.location };
  });

  // Combine built-in and custom destinations
  const allDestinations = [...SALE_DESTINATIONS, ...customDestinations];
  
  const groupedByDestination = allDestinations.map(dest => ({
    ...dest,
    vehicles: syncedRunList.filter(v => v.destination === dest.value)
  })).filter(g => g.vehicles.length > 0);

  // Group by category
  const groupedByCategory = [
    ...RUNLIST_CATEGORIES.map(cat => ({
      ...cat,
      vehicles: syncedRunList.filter(v => v.assignee === cat.value)
    })),
    { value: 'unassigned', label: 'Unassigned', color: 'bg-neutral-600', vehicles: syncedRunList.filter(v => !v.assignee || !RUNLIST_CATEGORIES.some(c => c.value === v.assignee)) }
  ].filter(g => g.vehicles.length > 0);

  const activeGroups = viewMode === 'category' ? groupedByCategory : groupedByDestination;

  const updateVehicleField = (vehicleId, field, value) => {
    if (onUpdate) onUpdate(vehicleId, field, value);
  };

  const printRunList = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Weekly Run List</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              background: #fff; 
              color: #000; 
              padding: 20px;
              font-size: 12px;
              line-height: 1.5;
            }
            .container { 
              border: 3px solid #000; 
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 { 
              font-size: 28px; 
              text-align: center; 
              margin-bottom: 5px;
              color: #000;
              font-weight: 900;
              letter-spacing: 2px;
            }
            .subtitle {
              text-align: center;
              color: #333;
              margin-bottom: 20px;
              font-size: 11px;
              font-weight: bold;
            }
            .section { 
              margin: 20px 0;
              border-top: 2px solid #000;
              padding-top: 15px;
            }
            .section-header { 
              font-size: 18px; 
              font-weight: 900;
              color: #000;
              padding: 5px 0;
              margin-bottom: 10px;
              border-bottom: 2px solid #000;
              text-transform: uppercase;
            }
            .vehicle-row {
              padding: 8px 0;
              border-bottom: 1px solid #999;
            }
            .stock { color: #000; font-weight: 900; font-size: 13px; }
            .vehicle-name { color: #000; font-weight: bold; }
            .needs { color: #000; font-weight: bold; text-decoration: underline; }
            .location { color: #333; font-weight: bold; }
            .notes { color: #333; }
            .summary {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 3px solid #000;
              color: #000;
              font-weight: bold;
            }
            .summary-line { margin: 5px 0; }
            @media print { 
              body { background: #fff !important; color: #000 !important; } 
              .stock, .vehicle-name, .needs, .section-header, h1, .summary { color: #000 !important; font-weight: 900 !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>WEEKLY RUN LIST</h1>
            <div class="subtitle">LOTSENSE DMS • Generated: ${new Date().toLocaleDateString()}</div>
            ${activeGroups.map(group => `
              <div class="section">
                <div class="section-header">${group.label.toUpperCase()}</div>
                ${group.vehicles.map(v => {
                  const catLabel = RUNLIST_CATEGORIES.find(c => c.value === v.assignee)?.label;
                  return `
                  <div class="vehicle-row">
                    <span class="stock">[${v.stockNumber}]</span>
                    <span class="vehicle-name">${v.year} ${v.make} ${v.model}</span>
                    ${viewMode === 'master' && catLabel ? ` <span style="color: #000; font-weight: 900;">[${catLabel}]</span>` : ''}
                    ${viewMode === 'category' ? ` <span style="color: #333; font-weight: bold;">→ ${v.destination || 'N/A'}</span>` : ''}<br/>
                    <span class="needs">NEEDS: ${Array.isArray(v.needs) ? v.needs.map(n => WORKFLOW_STATUSES.find(s => s.value === n)?.label || n).join(' / ') : (WORKFLOW_STATUSES.find(s => s.value === v.needs)?.label || v.needs)}</span><br/>
                    ${v.runListNotes ? `<span style="color: #000; font-weight: bold; font-style: italic;">TASK: ${v.runListNotes}</span><br/>` : ''}
                    ${v.notes ? `<span class="notes" style="color: #222; font-style: italic;">GAME PLAN: ${v.notes}</span><br/>` : ''}
                    <span class="location">LOC: ${v.location}</span>
                  </div>
                `;}).join('')}
                <div style="margin-top: 10px; color: #333; font-weight: bold;">TOTAL: ${group.vehicles.length} vehicle${group.vehicles.length !== 1 ? 's' : ''}</div>
              </div>
            `).join('')}
            <div class="summary">
              <div class="summary-line">═══════════════════════════════════════════</div>
              <div class="summary-line">SUMMARY</div>
              <div class="summary-line">Total Vehicles: ${runList.length}</div>
              ${SALE_DESTINATIONS.map(d => {
                const count = runList.filter(v => v.destination === d.value).length;
                return count > 0 ? `<div class="summary-line">${d.label}: ${count}</div>` : '';
              }).join('')}
            </div>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };

  // Calculate totals for financial summary
  const totalInvestment = runList.reduce((sum, v) => sum + (v.tiv || 0), 0);
  const totalPotentialProfit = runList.reduce((sum, v) => sum + (v.potentialProfit || 0), 0);

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-green-500">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-white font-mono">WEEKLY RUN LIST</h2>
          <div className="flex bg-neutral-800 rounded overflow-hidden">
            <button
              onClick={() => setViewMode('category')}
              className={`px-3 py-1 text-xs font-mono font-bold ${viewMode === 'category' ? 'bg-green-600 text-black' : 'text-neutral-400 hover:text-white'}`}
            >
              BY CATEGORY
            </button>
            <button
              onClick={() => setViewMode('master')}
              className={`px-3 py-1 text-xs font-mono font-bold ${viewMode === 'master' ? 'bg-green-600 text-black' : 'text-neutral-400 hover:text-white'}`}
            >
              MASTER LIST
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {showFinancialsOption && (
            <label className="flex items-center gap-2 text-green-400 font-mono text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showFinancials}
                onChange={(e) => setShowFinancials(e.target.checked)}
                className="rounded"
              />
              Show Financials
            </label>
          )}
          <div className="flex gap-2">
            <button
              onClick={printRunList}
              disabled={runList.length === 0}
              className="px-4 py-2 bg-green-600 text-black font-mono text-sm font-bold rounded hover:bg-green-500 disabled:opacity-50"
            >
              🖨️ PRINT
            </button>
            <button
              onClick={onClear}
              disabled={runList.length === 0}
              className="px-4 py-2 bg-red-600 text-white font-mono text-sm font-bold rounded hover:bg-red-500 disabled:opacity-50"
            >
              CLEAR ALL
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-neutral-700 text-white font-mono text-sm font-bold rounded hover:bg-neutral-600"
            >
              CLOSE
            </button>
          </div>
        </div>
      </div>

      {/* Terminal Content */}
      <div className="flex-1 overflow-y-auto p-6 font-mono text-sm">
        {runList.length === 0 ? (
          <div className="text-center text-green-500 py-20">
            <div className="text-2xl mb-2">[ EMPTY ]</div>
            <div className="text-neutral-500">No vehicles added to run list yet.</div>
            <div className="text-neutral-500">Click "+ Add" on any vehicle to get started.</div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="text-neutral-500 mb-4">LOTSENSE DMS • {new Date().toLocaleDateString()}</div>
            
            {activeGroups.map(group => (
              <div key={group.value} className="mb-8">
                <div className="text-green-400 text-xl font-bold mb-3 border-b border-green-500 pb-1">
                  {group.label.toUpperCase()}
                  <span className="text-neutral-500 text-sm ml-2">({group.vehicles.length})</span>
                </div>
                <div className="border-l-2 border-green-800 pl-4">
                  {group.vehicles.map(v => (
                    <div key={v.id} className="py-3 border-b border-neutral-800 flex justify-between items-start">
                      <div className="flex-1">
                        <div>
                          <span className="text-white font-bold">[{v.stockNumber}]</span>
                          <span className="text-green-400 ml-2">{v.year} {v.make} {v.model}</span>
                          {viewMode === 'category' && <span className="text-neutral-500 ml-2">→ {v.destination || 'N/A'}</span>}
                        </div>
                        <div className="text-yellow-400">NEEDS: {Array.isArray(v.needs) ? v.needs.map(n => WORKFLOW_STATUSES.find(s => s.value === n)?.label || n).join(' / ') : (WORKFLOW_STATUSES.find(s => s.value === v.needs)?.label || v.needs)}</div>
                        {v.notes && <div className="text-neutral-500 italic text-xs">GAME PLAN: {v.notes}</div>}
                        <div className="text-neutral-500">LOC: {v.location}</div>
                        <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                          <span className="text-neutral-600 text-xs mr-1">ASSIGN:</span>
                          {RUNLIST_CATEGORIES.map(cat => (
                            <button
                              key={cat.value}
                              onClick={() => updateVehicleField(v.id, 'assignee', v.assignee === cat.value ? '' : cat.value)}
                              className={`px-2 py-0.5 rounded text-xs font-bold font-mono transition ${v.assignee === cat.value ? cat.color + ' text-white' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}
                            >
                              {cat.label}
                            </button>
                          ))}
                        </div>
                        <div className="mt-1.5">
                          <input
                            type="text"
                            value={v.runListNotes || ''}
                            onChange={e => updateVehicleField(v.id, 'runListNotes', e.target.value)}
                            placeholder="Task notes..."
                            className="w-full bg-neutral-900 border border-green-800 text-green-300 font-mono text-xs px-2 py-1 rounded focus:outline-none focus:border-green-400"
                          />
                        </div>
                        {showFinancials && (
                          <div className="mt-1 text-xs grid grid-cols-5 gap-2">
                            <div><span className="text-neutral-600">COST:</span> <span className="text-cyan-400">${(v.deliveredCost || 0).toLocaleString()}</span></div>
                            <div><span className="text-neutral-600">RECON:</span> <span className="text-cyan-400">${(v.reconCost || 0).toLocaleString()}</span></div>
                            <div><span className="text-neutral-600">TIV:</span> <span className="text-cyan-400">${(v.tiv || 0).toLocaleString()}</span></div>
                            <div><span className="text-neutral-600">ASK:</span> <span className="text-cyan-400">${(v.askingPrice || 0).toLocaleString()}</span></div>
                            <div><span className="text-neutral-600">PROFIT:</span> <span className={v.potentialProfit >= 0 ? 'text-green-400' : 'text-red-400'}>${(v.potentialProfit || 0).toLocaleString()}</span></div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => onRemove(v.id)}
                        className="text-red-500 hover:text-red-400 text-xs ml-2"
                      >
                        [REMOVE]
                      </button>
                    </div>
                  ))}
                  <div className="text-neutral-500 mt-2">TOTAL: {group.vehicles.length} vehicle{group.vehicles.length !== 1 ? 's' : ''}</div>
                </div>
              </div>
            ))}

            {/* Summary */}
            <div className="border-t-2 border-green-500 pt-4 mt-8">
              <div className="text-white font-bold mb-2">═══════════════════════════════════════════</div>
              <div className="text-white font-bold">SUMMARY</div>
              <div className="text-green-400">Total Vehicles: {runList.length}</div>
              {SALE_DESTINATIONS.map(d => {
                const count = runList.filter(v => v.destination === d.value).length;
                return count > 0 ? (
                  <div key={d.value} className="text-green-400">{d.label}: {count}</div>
                ) : null;
              })}
              {showFinancials && (
                <div className="mt-4 pt-4 border-t border-green-800">
                  <div className="text-cyan-400">Total Investment: ${totalInvestment.toLocaleString()}</div>
                  <div className={totalPotentialProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                    Total Potential Profit: ${totalPotentialProfit.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Notes Modal Component
const EditNotesModal = ({ vehicle, initialNotes, onSave, onClose }) => {
  const [notes, setNotes] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(notes);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200">
          <h3 className="font-semibold text-lg">Edit Notes / Game Plan</h3>
          <p className="text-sm text-neutral-500">
            {vehicle.stockNumber} - {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
        </div>
        <div className="p-4">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter notes or game plan for this vehicle..."
            className="w-full h-40 px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          {vehicle.reconGamePlanUpdatedBy && (
            <p className="text-xs text-neutral-400 mt-2">
              Last updated by {vehicle.reconGamePlanUpdatedBy}
              {vehicle.reconGamePlanUpdatedAt && ` on ${formatDate(vehicle.reconGamePlanUpdatedAt)}`}
            </p>
          )}
        </div>
        <div className="p-4 border-t border-neutral-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Admin Todo Modal Component
const AdminTodoModal = ({ vehicle, todos, onAdd, onToggle, onDelete, onClose }) => {
  const [newText, setNewText] = useState('');
  const vehicleTodos = todos.filter(t => t.vehicleId === vehicle.id);
  const pending = vehicleTodos.filter(t => !t.done);
  const completed = vehicleTodos.filter(t => t.done);

  const handleAdd = () => {
    if (newText.trim()) {
      onAdd(vehicle.id, newText);
      setNewText('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center bg-neutral-50">
          <div>
            <h2 className="font-semibold text-base">Admin Tasks</h2>
            <p className="text-xs text-neutral-500">{vehicle.stockNumber} — {vehicle.year} {vehicle.make} {vehicle.model}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl px-2">×</button>
        </div>
        <div className="p-4 flex gap-2">
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="Add a task..."
            className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          <button
            onClick={handleAdd}
            disabled={!newText.trim()}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {pending.length === 0 && completed.length === 0 && (
            <div className="text-center py-6 text-neutral-400 text-sm">No tasks yet. Add one above.</div>
          )}
          {pending.length > 0 && (
            <div className="space-y-1 mb-3">
              {pending.map(t => (
                <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 group">
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => onToggle(t.id)}
                    className="rounded text-blue-600 flex-shrink-0"
                  />
                  <span className="flex-1 text-sm text-neutral-800">{t.text}</span>
                  <button
                    onClick={() => onDelete(t.id)}
                    className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
                  >×</button>
                </div>
              ))}
            </div>
          )}
          {completed.length > 0 && (
            <div>
              <div className="text-[10px] text-neutral-400 uppercase tracking-wider font-semibold mb-1">Completed</div>
              <div className="space-y-1">
                {completed.map(t => (
                  <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 group">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => onToggle(t.id)}
                      className="rounded text-green-600 flex-shrink-0"
                    />
                    <span className="flex-1 text-sm text-neutral-400 line-through">{t.text}</span>
                    <button
                      onClick={() => onDelete(t.id)}
                      className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowModule;